(function () {
  const PROMO_VARIANT_ID = (window.promoGiftConfig && window.promoGiftConfig.variantId) || 49198532362483;
  const THRESHOLD = (window.promoGiftConfig && window.promoGiftConfig.threshold) || 20000;
  const PROMO_ENABLED = (window.promoGiftConfig && window.promoGiftConfig.enabled) || false;

  if (!PROMO_ENABLED) return;

  let isCorrecting = false;

  function isPromoItem(item) {
    return item.variant_id === PROMO_VARIANT_ID;
  }

  function getQualifyingTotal(cart) {
    const promoTotal = cart.items
      .filter(isPromoItem)
      .reduce((total, item) => total + item.line_price, 0);
    return cart.total_price - promoTotal;
  }

  function getPromoQuantity(cart) {
    return cart.items
      .filter(isPromoItem)
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  // Same sections the theme's own gift-wrapping feature re-renders,
  // so the drawer, cart icon bubbles, and live region all stay in sync
  // from the single atomic response.
  function getSectionsToRender() {
    return [
      { id: 'mini-cart', section: 'mini-cart', selector: '.shopify-section' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' }
    ];
  }

  // Core atomic update via /cart/update.js — the same endpoint and
  // pattern the theme's own gift-wrapping feature uses. Variant-keyed,
  // absolute quantities, so nothing can go stale and duplicates are
  // impossible. Section rendering is optional: the background flow
  // renders the drawer from the same response; the checkout guard
  // skips rendering because it navigates away immediately, and
  // re-rendering would replace the very form it's about to resubmit.
  function cartUpdate(updates, withSections) {
    const payload = { updates: updates };
    if (withSections) {
      payload.sections = getSectionsToRender().map((s) => s.section);
      payload.sections_url = window.location.pathname;
    }
    const body = JSON.stringify(payload);

    const config = (typeof fetchConfig === 'function')
      ? { ...fetchConfig(), body }
      : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body
        };

    const updateUrl = (window.theme && theme.routes && theme.routes.cart_update_url) || '/cart/update.js';

    return fetch(updateUrl, config).then((response) => response.json());
  }

  function setPromoQuantity(quantity) {
    return cartUpdate({ [PROMO_VARIANT_ID]: quantity }, true).then((response) => {
      const miniCart = document.querySelector('mini-cart');
      if (miniCart && response.sections) {
        miniCart.renderContents(response);
      }
      return response;
    });
  }

  // Runs after the theme has finished its own cart update (see the
  // subscription below), so it never executes in parallel with the
  // theme's quantity requests. Fetches the fresh cart, decides the
  // correct promo quantity, and issues at most one atomic update.
  function correctPromo() {
    if (isCorrecting) return;
    isCorrecting = true;

    fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => {
        const eligible = getQualifyingTotal(cart) >= THRESHOLD;
        const currentQuantity = getPromoQuantity(cart);
        const targetQuantity = eligible ? 1 : 0;

        if (currentQuantity !== targetQuantity) {
          return setPromoQuantity(targetQuantity);
        }
      })
      .catch((err) => console.error('Promo gift correction failed:', err))
      .finally(() => {
        isCorrecting = false;
      });
  }

  // Subscribe to the theme's own PubSub cartUpdate event. It fires
  // after the theme's cart logic completes an update, which is exactly
  // the safe moment to apply a correction — never in parallel with it.
  // Falls back to the drawer-opened event if the PubSub helpers are
  // unavailable for any reason.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => correctPromo());
  } else {
    document.addEventListener('cart:updated', correctPromo);
  }

  document.addEventListener('cartdrawer:opened', correctPromo);
  document.addEventListener('DOMContentLoaded', correctPromo);

  // Minimal checkout guard — belt and suspenders. The PubSub-driven
  // correction above should always leave the cart correct before a
  // checkout is possible, but this is the one scenario that produced
  // real bad orders originally, so it gets explicit protection anyway.
  // It intercepts the cart form's checkout submission once, awaits a
  // single atomic correction (a no-op request if the cart is already
  // correct), then re-submits with a flag so it never intercepts its
  // own resubmission. form.submit() is not used because it drops the
  // checkout button's name/value (Shopify would route to /cart);
  // requestSubmit(button) preserves it, and the guard flag prevents
  // the re-entry loop that method caused in earlier iterations.
  let checkoutCorrected = false;

  document.addEventListener('submit', async (event) => {
    const formEl = event.target;
    if (!(formEl instanceof HTMLFormElement)) return;
    const checkoutButton = formEl.querySelector('button[name="checkout"]');
    if (!checkoutButton) return;

    if (checkoutCorrected) {
      checkoutCorrected = false;
      return; // our own resubmission — let it through untouched
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      // Step 1: flush the customer's on-screen quantities to the server.
      // The theme debounces quantity changes ~300ms before sending them,
      // so a fast checkout click can arrive while the server still has
      // the OLD quantities — which is exactly how an ineligible cart
      // previously slipped through with the gift (the guard asked the
      // server and got a truthful but outdated answer). The on-screen
      // inputs reflect the customer's intent instantly, so the guard
      // pushes those itself, atomically, keyed by variant ID.
      const updates = {};
      document.querySelectorAll('input[data-quantity-variant-id]').forEach((input) => {
        const variantId = input.dataset.quantityVariantId;
        const qty = parseInt(input.value, 10);
        if (variantId && !Number.isNaN(qty)) {
          updates[variantId] = qty;
        }
      });
      delete updates[String(PROMO_VARIANT_ID)];

      const cart = Object.keys(updates).length > 0
        ? await cartUpdate(updates, false)
        : await fetch('/cart.js').then((res) => res.json());

      // Step 2: judge eligibility on that fresh, intent-accurate cart
      // and correct the gift if needed. No section rendering here —
      // we're navigating to checkout immediately, and re-rendering
      // would replace the form we're about to resubmit.
      const eligible = getQualifyingTotal(cart) >= THRESHOLD;
      const currentQuantity = getPromoQuantity(cart);
      const targetQuantity = eligible ? 1 : 0;

      if (currentQuantity !== targetQuantity) {
        await cartUpdate({ [PROMO_VARIANT_ID]: targetQuantity }, false);
      }
    } catch (err) {
      console.error('Promo checkout guard failed:', err);
    }

    checkoutCorrected = true;
    if (typeof formEl.requestSubmit === 'function') {
      formEl.requestSubmit(checkoutButton);
    } else {
      formEl.submit();
    }
  }, true);
})();